<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row" v-if="userData.permissions['view_loan_products'] == 1">
      <div class="col-12">
        <div class="card">
          <div class="card-body">

            <!-- Top Header & Search/Add Actions -->
            <div class="row mb-4 align-items-center">
              <div class="col-sm-12 col-md-6">
                <div class="d-flex align-items-center">
                  <label class="d-inline-flex align-items-center mb-0 me-3">
                    Show
                    <b-form-select class="form-select form-select-sm mx-2" v-model="perPage" size="sm"
                      :options="Options"></b-form-select>
                    entries
                  </label>
                  <div class="d-inline-flex align-items-center mx-3">
                    Search:
                    <b-form-input v-model="filter" type="search" placeholder="Search products..."
                      class="form-control form-control-sm ms-2"></b-form-input>
                  </div>
                </div>
              </div>

              <div class="col-sm-12 col-md-6 text-md-end mt-3 mt-md-0">
                <b-button size="sm" variant="primary" @click="promptCreateProduct" v-if="List > 0 || isBusy">
                  <i class="mdi mdi-plus"></i> New Loan Product
                </b-button>
              </div>
            </div>

            <!-- Table -->
            <div class="table-responsive">
              <b-table :items="Items" :fields="Fields" responsive="sm" :per-page="perPage" :current-page="currentPage"
                :filter="filter" @filtered="Filtered" :busy="IsBusy" show-empty
                class="align-middle table-nowrap table-hover">

                <template #table-busy>
                  <div class="text-center text-primary my-5">
                    <b-spinner class="align-middle" type="grow"></b-spinner>
                    <div class="mt-2 fw-medium">Loading Loan Products...</div>
                  </div>
                </template>

                <template #empty>
                  <div class="text-center py-5">
                    <div class="mb-3">
                      <i class="mdi mdi-text-box-remove-outline text-muted" style="font-size: 64px;"></i>
                    </div>
                    <h5 class="text-dark">No Loan Products Available</h5>
                    <p class="text-muted mb-4">You haven't configured any loan products yet.</p>
                    <b-button variant="primary" @click="promptCreateProduct" class="px-4">
                      <i class="mdi mdi-plus me-1"></i> Add Your First Product
                    </b-button>
                  </div>
                </template>

                <template #cell(index)="data">{{ data.index + 1 }}</template>
                <template #cell(name)="data">
                  <span class="fw-bold text-dark">{{ data.item.name }}</span>
                  <small class="d-block">{{ data.item.description }}</small>
                </template>

                <template #cell(limits)="data">
                  <small class="text-muted">Min:</small> {{ commas(data.item.min_amount) }} <br>
                  <small class="text-muted">Max:</small> {{ commas(data.item.max_amount) }}
                </template>

                <template #cell(terms)="data">
                  {{ data.item.period }} Months <br>
                  <small class="text-info">{{ data.item.grace_period }} Days Grace</small>
                </template>

                <template #cell(interest)="data">
                  <b-badge class="p-1" variant="soft-info">{{ data.item.rate }}% {{ data.item.rate_type }}</b-badge>
                  <span v-if="data.item.has_loan_charges"> + </span>
                  <b-badge class="p-1" variant="soft-secondary" v-if="data.item.has_loan_charges">Charges</b-badge><br>
                  <small v-if="data.item.interest_type !== 'REDUCING_BALANCE'" class="text-muted">{{
                    data.item.interest_type.replace('_', ' ') }}</small>
                  <small v-else class="text-muted">{{ data.item.interest_type.replace('_', ' ') }}</small>
                </template>

                <template #cell(status)="data">
                  <b-badge class="p-1 fw-bold"
                    :class="{ 'bg-primary': data.item.status == 'ACTIVE', 'bg-secondary': data.item.status == 'INACTIVE' }">
                    {{ data.item.status }}
                  </b-badge>
                </template>

                <template #cell(actions)="data">
                  <div class="d-flex gap-2">
                    <b-button title="Edit" size="sm" variant="info" @click="promptEditProduct(data.item)">
                      <i class="mdi mdi-pencil"></i>
                    </b-button>
                    <b-button v-if="data.item.status == 'INACTIVE'" title="Activate" size="sm" variant="primary"
                      @click="promptActivate(data.item.id)">
                      <i class="mdi mdi-check-bold"></i>
                    </b-button>
                    <b-button v-else title="Deactivate" size="sm" variant="secondary"
                      @click="promptDeactivate(data.item.id)">
                      <i class="mdi mdi-close"></i>
                    </b-button>
                    <b-button title="Delete" size="sm" variant="danger" @click="promptDelete(data.item.id)">
                      <i class="mdi mdi-trash-can"></i>
                    </b-button>
                  </div>
                </template>
              </b-table>
            </div>

            <div class="row align-items-center" v-if="List > 0">
              <div class="col-sm-12 col-md-6">
                <div class="text-muted">
                  Showing {{ ((currentPage - 1) * perPage) + 1 }} to {{ Math.min(currentPage * perPage, TotalRows) }} of
                  {{
                    TotalRows }} entries
                </div>
              </div>
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <b-pagination v-model="currentPage" :total-rows="TotalRows" :per-page="perPage"></b-pagination>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Permission Denied -->
    <div v-else>
      <div class="row justify-content-center">
        <div class="col-md-8 col-xl-6">
          <div class="text-center py-5 card card-body shadow-sm">
            <img src="@/assets/images/profile-img.png" alt="No Permission" class="img-fluid mx-auto d-block"
              style="max-width: 200px;" /><br>
            <h5 class="text-primary mt-3">Access Denied</h5>
            <span class="text-muted">You do not have the required permissions to view Loan Products.</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Form Wizard Modal -->
    <b-modal id="product-modal" :title="editMode ? 'Edit Loan Product' : 'Create New Loan Product'" hide-footer centered
      size="lg">

      <!-- Wizard Progress (4 Steps) -->
      <div class="d-flex justify-content-between mb-4 position-relative px-2">
        <div class="progress position-absolute" style="height: 4px; top: 16px; left: 10%; right: 10%; z-index: 1;">
          <div class="progress-bar bg-primary" :style="{ width: ((wizardStep - 1) * 33.33) + '%' }"></div>
        </div>

        <div class="text-center position-relative bg-white px-1" style="z-index: 2;">
          <b-button :variant="wizardStep >= 1 ? 'primary' : 'light'" class="rounded-circle fw-bold shadow-sm border-0"
            style="width: 36px; height: 36px; padding: 0;">1</b-button>
          <div class="mt-2 small fw-medium" :class="wizardStep >= 1 ? 'text-dark' : 'text-muted'">Basic Info</div>
        </div>
        <div class="text-center position-relative bg-white px-1" style="z-index: 2;">
          <b-button :variant="wizardStep >= 2 ? 'primary' : 'light'" class="rounded-circle fw-bold shadow-sm border-0"
            style="width: 36px; height: 36px; padding: 0;">2</b-button>
          <div class="mt-2 small fw-medium" :class="wizardStep >= 2 ? 'text-dark' : 'text-muted'">Interest</div>
        </div>
        <div class="text-center position-relative bg-white px-1" style="z-index: 2;">
          <b-button :variant="wizardStep >= 3 ? 'primary' : 'light'" class="rounded-circle fw-bold shadow-sm border-0"
            style="width: 36px; height: 36px; padding: 0;">3</b-button>
          <div class="mt-2 small fw-medium" :class="wizardStep >= 3 ? 'text-dark' : 'text-muted'">Limits & Grace</div>
        </div>
        <div class="text-center position-relative bg-white px-1" style="z-index: 2;">
          <b-button :variant="wizardStep >= 4 ? 'primary' : 'light'" class="rounded-circle fw-bold shadow-sm border-0"
            style="width: 36px; height: 36px; padding: 0;">4</b-button>
          <div class="mt-2 small fw-medium" :class="wizardStep >= 4 ? 'text-dark' : 'text-muted'">Penalties</div>
        </div>
      </div>

      <form ref="productForm" @submit.prevent>

        <!-- STEP 1: Basic Info -->
        <div v-if="wizardStep === 1">
          <div class="form-group mb-4">
            <label class="form-label mb-0">Product Name <span class="text-danger">*</span></label>
            <input type="text" v-model="form.name" class="form-control" placeholder="e.g., Business Salary Loan"
              required>
          </div>
          <div class="form-group mb-4">
            <label class="form-label mb-0">Description (Optional)</label>
            <textarea v-model="form.description" class="form-control" rows="3"
              placeholder="Briefly describe this loan product..."></textarea>
          </div>
        </div>

        <!-- STEP 2: Loan Interest & Terms -->
        <div v-if="wizardStep === 2">
          <div class="row">
            <div class="col-md-6 form-group mb-4">
              <label class="form-label mb-0">Interest Type <span class="text-danger">*</span></label>
              <!-- Updated with human-readable labels -->
              <select v-model="form.interest_type" class="form-select" required>
                <option value="SIMPLE">Simple Interest</option>
                <option value="COMPOUND">Compound Interest (Lump Sum at End)</option>
                <option value="REDUCING_BALANCE">Reducing Balance (Monthly Installments)</option>
                <option value="FIXED">Fixed Flat Interest</option>
              </select>
            </div>
            <div class="col-md-6 form-group mb-4">
              <label class="form-label mb-0">Rate Type <span class="text-danger">*</span></label>
              <select v-model="form.rate_type" class="form-select" required>
                <option value="MONTHLY">Monthly</option>
                <option value="ANNUALLY">Annually</option>
              </select>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6 form-group mb-4">
              <label class="form-label mb-0">Interest Rate (%) <span class="text-danger">*</span></label>
              <input type="number" v-model="form.rate" class="form-control" step="0.1" min="0" placeholder="e.g., 5"
                required>
            </div>
            <div class="col-md-6 form-group mb-4">
              <label class="form-label mb-0">Loan Period (Months) <span class="text-danger">*</span></label>
              <input type="number" v-model="form.period" class="form-control" min="1" placeholder="e.g., 12" required>
            </div>
          </div>

          <!-- Dynamic Calculation Example Box -->
          <div class="mt-2 p-3 bg-soft-primary rounded border border-primary shadow-sm transition-all"
            v-if="exampleData">
            <h6 class="text-primary mb-3"><i class="mdi mdi-calculator me-1"></i> Example Calculation</h6>
            <p class="mb-2 text-dark font-size-13">
              Assuming a principal of <strong>{{ userData.currency }} 1,000,000</strong>:
            </p>

            <div v-if="form.interest_type === 'SIMPLE'" class="font-size-13 text-muted mb-3">
              <strong>Formula:</strong> <em>Total Interest = Principal &times; Rate &times; Time</em>
            </div>

            <div v-if="form.interest_type === 'COMPOUND'" class="font-size-13 text-muted mb-3">
              <strong>Formula:</strong> <em>Total Amount = Principal &times; (1 + Rate)<sup>Time</sup></em>
            </div>

            <div v-if="form.interest_type === 'REDUCING_BALANCE'" class="font-size-13 text-muted mb-3">
              <strong>Formula (EMI):</strong> <em>EMI = [Principal &times; Rate &times; (1 + Rate)<sup>Time</sup>] / [(1
                + Rate)<sup>Time</sup> - 1]</em>
              <div class="text-info mt-1"><i class="mdi mdi-information-outline"></i> <strong>Note:</strong> Reducing
                balance interest depends on the repayment timeline. The range below represents the <strong>best
                  case</strong> (paying off entirely in month 1) to the <strong>worst case</strong> (paying off entirely
                at
                the end of the term).</div>
            </div>

            <div v-if="form.interest_type === 'FIXED'" class="font-size-13 text-muted mb-3">
              <strong>Formula:</strong> <em>Total Interest = Principal &times; Flat Rate</em>
              <div class="text-info mt-1"><i class="mdi mdi-information-outline"></i> <strong>Note:</strong> The loan
                period
                does not affect Fixed Flat interest. The interest remains the same regardless of how long the loan runs.
              </div>
            </div>

            <div class="row border-top border-primary pt-2 mt-2 font-size-14">
              <div class="col-6">
                Total Interest:<br>
                <strong class="text-danger">
                  <span v-if="Array.isArray(exampleData.interest)">
                    {{ userData.currency }} {{ commas(exampleData.interest[0].toFixed(0)) }} - {{
                      commas(exampleData.interest[1].toFixed(0)) }}
                  </span>
                  <span v-else>
                    {{ userData.currency }} {{ commas(exampleData.interest.toFixed(0)) }}
                  </span>
                </strong>
              </div>
              <div class="col-6 text-end">
                Total Repayment:<br>
                <strong class="text-success">
                  <span v-if="Array.isArray(exampleData.total)">
                    {{ userData.currency }} {{ commas(exampleData.total[0].toFixed(0)) }} - {{
                      commas(exampleData.total[1].toFixed(0)) }}
                  </span>
                  <span v-else>
                    {{ userData.currency }} {{ commas(exampleData.total.toFixed(0)) }}
                  </span>
                </strong>
              </div>
            </div>
            <div v-if="exampleData.emi > 0" class="row mt-2 font-size-14">
              <div class="col-12 text-center bg-white py-1 rounded">
                Estimated Monthly Installment: <strong>{{ userData.currency }} {{ commas(exampleData.emi.toFixed(0))
                  }}</strong>
              </div>
            </div>
          </div>

        </div>

        <!-- STEP 3: Limits & Grace -->
        <div v-if="wizardStep === 3">
          <div class="row">
            <div class="col-md-6 form-group mb-4">
              <label class="form-label mb-0">Min Amount ({{ userData.currency }}) <span
                  class="text-danger">*</span></label>
              <br>

              <small class="block">The minimum amount that can be borrowed for this loan product.</small>
              <input v-number-format type="text" inputmode="numeric" v-model="form.min_amount" class="form-control"
                placeholder="e.g 100,000" required>
            </div>
            <div class="col-md-6 form-group mb-4">
              <label class="form-label mb-0">Max Amount ({{ userData.currency }}) <span
                  class="text-danger">*</span></label>
              <br>
              <small class="block">The maximum amount that can be borrowed for this loan product.</small>
              <input v-number-format type="text" inputmode="numeric" v-model="form.max_amount" class="form-control"
                placeholder="e.g 5,000,000" required>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12 form-group mb-4">
              <label class="form-label mb-0">Grace Period (Days) <span class="text-danger">*</span></label>
              <input type="number" v-model="form.grace_period" class="form-control" min="0"
                placeholder="Number of penalty-free days after expiry" required>
              <div class="form-text text-muted">The time after loan expiration when the borrower can still repay without
                penalty.</div>
            </div>
          </div>
        </div>

        <!-- STEP 4: Penalties & Rules -->
        <div v-if="wizardStep === 4">

          <!-- Fine Structure Panel -->
          <div class="card border shadow-none mb-4">
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <div class="flex-shrink-0 me-3">
                  <div class="avatar-sm">
                    <span class="avatar-title bg-soft-danger text-white rounded-circle fs-4">
                      <i class="mdi mdi-alert-octagon-outline"></i>
                    </span>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <h5 class="font-size-15 mb-1">Late Repayment Fines</h5>
                  <p class="text-muted font-size-13 mb-0">Apply penalties to this product for missed or delayed
                    payments.
                  </p>
                </div>
              </div>

              <div class="mt-3">
                <div class="input-group">
                  <label class="input-group-text bg-light"><i class="mdi mdi-gavel text-muted"></i></label>
                  <select v-model="form.loan_fine_id" class="form-select fw-medium" style="cursor: pointer;">
                    <option :value="null">-- No Fines Applied --</option>
                    <option v-for="fine in activeFines" :key="fine.id" :value="fine.id">
                      {{ fine.type.replace(/_/g, ' ') }}
                      ({{ fine.percentage ? fine.percentage + '%' : userData.currency + ' ' + commas(fine.fixed_amount)
                      }})
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- System Charges Toggle Panel -->
          <div class="card border shadow-none mb-4 transition-all"
            :class="form.has_loan_charges ? 'border-primary bg-soft-primary' : 'border-light'">
            <div class="card-body">
              <div class="d-flex align-items-center"> <!-- Icon -->
                <div class="flex-shrink-0 me-3">
                  <div class="avatar-sm"> <span class="avatar-title rounded-circle fs-4 transition-all"
                      :class="form.has_loan_charges ? 'bg-primary text-white' : 'bg-soft-secondary text-secondary'"> <i
                        class="mdi mdi-cash-multiple"></i> </span> </div>
                </div> <!-- Description -->
                <div class="flex-grow-1">
                  <h5 class="font-size-15 mb-1" :class="{ 'text-primary': form.has_loan_charges }"> System Loan Charges
                  </h5>
                  <p class="font-size-13 mb-0" :class="form.has_loan_charges ? 'text-dark' : 'text-muted'">
                    Automatically
                    deduct tiered origination fees defined in the <em>Loan Charges</em> module during loan disbursement.
                  </p>
                </div> <!-- Status + Toggle -->
                <div class="flex-shrink-0 ms-4">
                  <div class="loan-charge-toggle" :class="{ active: form.has_loan_charges }"
                    @click="form.has_loan_charges = !form.has_loan_charges">
                    <div class="toggle-status"> <span class="status-dot"
                        :class="form.has_loan_charges ? 'bg-success' : 'bg-secondary'"></span> <span
                        class="status-text"> {{
                          form.has_loan_charges ? 'Enabled' : 'Disabled' }} </span> </div>
                    <div class="custom-toggle" :class="{ checked: form.has_loan_charges }">
                      <div class="toggle-knob"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Wizard Navigation Footer -->
        <hr />
        <div class="d-flex justify-content-between mt-3">
          <b-button v-if="wizardStep > 1" variant="light" @click="wizardStep--"><i class="mdi mdi-arrow-left"></i>
            Back</b-button>
          <div v-else></div> <!-- Spacer -->

          <div class="d-flex gap-2">
            <b-button variant="light" @click="$bvModal.hide('product-modal')">Cancel</b-button>
            <b-button v-if="wizardStep < 4" variant="primary" @click="nextStep">Next <i
                class="mdi mdi-arrow-right"></i></b-button>
            <b-button v-if="wizardStep === 4" @click.prevent="saveProduct" variant="primary" :disabled="isActionLoading">
              <b-spinner small v-if="isActionLoading" class="me-1"></b-spinner><i v-else class="mdi mdi-content-save me-1"></i>
              {{ isActionLoading ? 'Saving...' : (editMode ? 'Update Product' : 'Save Product') }}</b-button>
          </div>
        </div>
      </form>
    </b-modal>

    <!-- Confirmation Modals -->
    <b-modal id="activate-modal" hide-footer centered hide-header>
      <div class="text-center my-4">
        <div class="display-4 text-primary mb-2"><i class="mdi mdi-check-circle-outline"></i></div>
        <h4 class="text-primary mb-3">Activate Product?</h4>
        <p class="fs-6 text-muted mb-4">Activating this product makes it available for new loan applications.</p>
      </div>
      <div class="d-flex justify-content-center mt-4">
        <b-button variant="light" @click="$bvModal.hide('activate-modal')" class="me-3 px-4">Cancel</b-button>
        <b-button variant="primary" @click="executeActivate" class="px-4" :disabled="isActionLoading">
          <b-spinner small v-if="isActionLoading" class="me-1"></b-spinner>
          <i v-else class="mdi mdi-check-bold me-1"></i>
          {{ isActionLoading ? 'Activating...' : 'Yes, Activate' }}
        </b-button>
      </div>
    </b-modal>

    <b-modal id="deactivate-modal" hide-footer centered hide-header>
      <div class="text-center my-4">
        <div class="display-4 text-secondary mb-2"><i class="mdi mdi-close-circle-outline"></i></div>
        <h4 class="text-secondary mb-3">Deactivate Product?</h4>
        <p class="fs-6 text-muted mb-4">Deactivating this product hides it from new applications. Existing loans are
          unaffected.</p>
      </div>
      <div class="d-flex justify-content-center mt-4">
        <b-button variant="light" @click="$bvModal.hide('deactivate-modal')" class="me-3 px-4">Cancel</b-button>
        <b-button variant="secondary" @click="executeDeactivate" class="px-4" :disabled="isActionLoading">
          <b-spinner small v-if="isActionLoading" class="me-1"></b-spinner>
          <i v-else class="mdi mdi-close me-1"></i>
          {{ isActionLoading ? 'Deactivating...' : 'Yes, Deactivate' }}
        </b-button>
      </div>
    </b-modal>

    <b-modal id="delete-modal" hide-footer centered hide-header>
      <div class="text-center my-4">
        <div class="display-4 text-danger mb-2"><i class="mdi mdi-trash-can-outline"></i></div>
        <h4 class="text-danger mb-3">Delete Product?</h4>
        <p class="fs-6 text-muted mb-4">This product will be archived. Historic reports will be retained safely.</p>
      </div>
      <div class="d-flex justify-content-center mt-4">
        <b-button variant="light" @click="$bvModal.hide('delete-modal')" class="me-3 px-4">Cancel</b-button>
        <b-button variant="danger" @click="executeDelete" class="px-4" :disabled="isActionLoading"><b-spinner small v-if="isActionLoading" class="me-1"></b-spinner><i v-else class="mdi mdi-trash-can me-1"></i>
          {{ isActionLoading ? 'Deleting...' : 'Delete' }}</b-button>
      </div>
    </b-modal>

  </Layout>
</template>

<script>
import axios from "axios";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { loanproductMethods, loanproductComputed, loanfinesMethods, loanfinesComputed } from "@/state/helpers";

export default {
  page: { title: "Loan Products", meta: [{ name: "description", content: appConfig.description }] },
  components: { Layout, PageHeader },
  directives: {
    numberFormat: {
      bind(el) {
        function handler() {
          if (el._formatting) return;
          const digits = el.value.replace(/\D/g, '');
          const formatted = digits ? parseInt(digits, 10).toLocaleString('en-US') : '';
          if (el.value !== formatted) {
            el._formatting = true;
            el.value = formatted;
            el.dispatchEvent(new Event('input', { bubbles: true }));
            el._formatting = false;
          }
        }
        el.addEventListener('input', handler);
        el._numberFormatHandler = handler;
      },
      inserted(el) { el._numberFormatHandler(); },
      componentUpdated(el) { el._numberFormatHandler(); },
      unbind(el) { el.removeEventListener('input', el._numberFormatHandler); }
    }
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")),
      title: "Loan Products",
      items: [{ text: "Dashboard", to: "/" }, { text: "Loan Products", active: true }],
      TotalRows: 0,
      currentPage: 1,
      perPage: 10,
      Options: [10, 50, 100, 150, 200],
      filter: null,
      isBusy: true,
      isActionLoading: false,

      wizardStep: 1,
      editMode: false,
      selectedId: null,

      fields: [
        { key: 'index', label: '#', visible: true, class: 'align-middle' },
        { key: 'id', label: 'ID', visible: false },
        { key: 'name', label: 'Product Name', visible: true, class: 'align-middle' },
        { key: 'limits', label: 'Amount Limits', visible: true, class: 'align-middle' },
        { key: 'terms', label: 'Terms', visible: true, class: 'align-middle' },
        { key: 'interest', label: 'Interest', visible: true, class: 'align-middle' },
        { key: 'status', label: 'Status', visible: true, class: 'align-middle' },
        { key: 'actions', label: 'Actions', visible: true, class: 'align-middle text-end' },
      ],

      form: {
        name: '', description: '', min_amount: null, max_amount: null,
        interest_type: 'SIMPLE', rate: null, rate_type: 'MONTHLY',
        period: null, grace_period: 0, loan_fine_id: null, has_loan_charges: false
      }
    };
  },
  computed: {
    ...loanproductComputed,
    ...loanfinesComputed,
    List() { return this.loanproducts ? this.loanproducts.length : 0; },
    Fields() { return this.fields.filter(field => field.visible); },
    Items() { return this.loanproducts ? this.loanproducts.map((d) => ({ ...d })) : []; },
    IsBusy() { return this.isBusy; },

    activeFines() {
      return this.loanfines ? this.loanfines.filter(fine => fine.status === 'ACTIVE') : [];
    },

    exampleData() {
      const P = 1000000;
      const rate = parseFloat(this.form.rate);
      const period = parseInt(this.form.period);

      if (isNaN(rate) || isNaN(period) || rate <= 0 || period <= 0) return null;

      const r_m = this.form.rate_type === 'ANNUALLY' ? (rate / 12) / 100 : rate / 100;

      let totalInterest = 0;
      let totalAmount = 0;
      let emi = 0;

      switch (this.form.interest_type) {
        case 'SIMPLE':
          totalInterest = P * r_m * period;
          totalAmount = P + totalInterest;
          if (period > 0) emi = totalAmount / period;
          break;

        case 'COMPOUND':
          totalAmount = P * Math.pow((1 + r_m), period);
          totalInterest = totalAmount - P;
          if (period > 0) emi = totalAmount / period;
          break;

        case 'REDUCING_BALANCE':
          if (r_m > 0) {
            // Standard expected EMI for reference
            const emiMonthly = P * r_m * (Math.pow(1 + r_m, period)) / (Math.pow(1 + r_m, period) - 1);

            // Best case: pays all money in the first month
            const minInterest = P * r_m;

            // Worst case: pays all money at the last month (compounded)
            const maxInterest = (P * Math.pow((1 + r_m), period)) - P;

            totalInterest = [minInterest, maxInterest];
            totalAmount = [P + minInterest, P + maxInterest];
            emi = emiMonthly;
          } else {
            totalAmount = P;
            emi = P / period;
            totalInterest = [0, 0];
          }
          break;

        case 'FIXED':
          // Period does not matter, rate is applied directly to P
          totalInterest = P * (rate / 100);
          totalAmount = P + totalInterest;
          if (period > 0) emi = totalAmount / period;
          break;
      }

      return {
        interest: totalInterest,
        total: totalAmount,
        emi: emi
      };
    }
  },
  async mounted() {
    await this.fetchLoanFines();
    await this.fetchLoanProducts();
    this.TotalRows = this.List;
    this.isBusy = false;
  },
  watch: {
    List(newVal) { this.TotalRows = newVal; }
  },
  methods: {
    ...loanproductMethods,
    ...loanfinesMethods,
    commas(x) { return x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : 0; },
    Filtered(filteredItems) {
      this.TotalRows = filteredItems.length;
      this.currentPage = 1;
    },

    nextStep() {
      const productForm = this.$refs.productForm;

      if (!productForm.checkValidity()) {
        productForm.reportValidity();
        return;
      }

      if (this.wizardStep === 1 && !this.form.name.trim()) {
        this.$toast.error("Product name is required.");
        return;
      }

      if (this.wizardStep === 3) {
        const min = Number(String(this.form.min_amount).replace(/,/g, ''));
        const max = Number(String(this.form.max_amount).replace(/,/g, ''));

        if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
          this.$toast.error("Max amount must be greater than Min amount.");
          return;
        }
      }

      this.wizardStep++;
    },

    resetForm() {
      this.form = {
        name: '', description: '', min_amount: null, max_amount: null,
        interest_type: 'SIMPLE', rate: null, rate_type: 'MONTHLY',
        period: null, grace_period: 0, loan_fine_id: null, has_loan_charges: false
      };
      this.wizardStep = 1;
    },

    promptCreateProduct() {
      this.editMode = false;
      this.selectedId = null;
      this.resetForm();
      this.$bvModal.show('product-modal');
    },

    promptEditProduct(item) {
      this.editMode = true;
      this.selectedId = item.id;
      this.form = {
        name: item.name,
        description: item.description,
        min_amount: item.min_amount,
        max_amount: item.max_amount,
        interest_type: item.interest_type,
        rate: item.rate,
        rate_type: item.rate_type,
        period: item.period,
        grace_period: item.grace_period,
        loan_fine_id: item.loan_fine_id,
        has_loan_charges: item.has_loan_charges == 1
      };
      this.wizardStep = 1;
      this.$bvModal.show('product-modal');
    },

    async saveProduct() {
      const payload = {
        ...this.form,
        min_amount: Number(String(this.form.min_amount).replace(/,/g, '')),
        max_amount: Number(String(this.form.max_amount).replace(/,/g, '')),
      };

      this.isActionLoading = true;
      try {
        if (this.editMode) {
          payload.id = this.selectedId;
          await axios.post('/update-loan-product', payload);
          this.$toast.success('Loan product updated successfully!');
        } else {
          await axios.post('/add-loan-product', payload);
          this.$toast.success('Loan product created successfully!');
        }

        this.$bvModal.hide('product-modal');
        this.isBusy = true;
        await this.fetchLoanProducts();
        this.isBusy = false;
      } catch (error) {
        this.$toast.error('Failed to save loan product. Check your inputs.');
      } finally {
        this.isActionLoading = false;
      }
    },

    promptActivate(id) { this.selectedId = id; this.$bvModal.show('activate-modal'); },
    promptDeactivate(id) { this.selectedId = id; this.$bvModal.show('deactivate-modal'); },
    promptDelete(id) { this.selectedId = id; this.$bvModal.show('delete-modal'); },

    async executeActivate() {
      this.isActionLoading = true;
      try {
        await axios.post('/activate-loan-product', { id: this.selectedId });
        this.$toast.success('Product activated!');
        this.isBusy = true; await this.fetchLoanProducts(); this.isBusy = false;
      } catch (e) {
        this.$toast.error('Action failed.');
      } finally {
        this.isActionLoading = false;
      }
      this.$bvModal.hide('activate-modal');
    },

    async executeDeactivate() {
      this.isActionLoading = true;
      try {
        await axios.post('/deactivate-loan-product', { id: this.selectedId });
        this.$toast.success('Product deactivated!');
        this.isBusy = true; await this.fetchLoanProducts(); this.isBusy = false;
      } catch (e) {
        this.$toast.error('Action failed.');
      } finally {
        this.isActionLoading = false;
      }
      this.$bvModal.hide('deactivate-modal');
    },

    async executeDelete() {
      this.isActionLoading = true;
      try {
        await axios.post('/delete-loan-product', { id: this.selectedId });
        this.$toast.success('Product deleted successfully!');
        this.isBusy = true; await this.fetchLoanProducts(); this.isBusy = false;
      } catch (e) {
        this.$toast.error('Action failed.');
      } finally {
        this.isActionLoading = false;
      }
      this.$bvModal.hide('delete-modal');
    }
  }
};
</script>

<style scoped>
.loan-charge-toggle {
  min-width: 125px;
  padding: 8px 10px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.loan-charge-toggle:hover {
  border-color: #adb5bd;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.loan-charge-toggle.active {
  border-color: var(--bs-primary);
  background: rgba(var(--bs-primary-rgb), 0.05);
}

.toggle-status {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
}

.status-text {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.custom-toggle {
  width: 38px;
  height: 22px;
  border-radius: 20px;
  background: #adb5bd;
  padding: 2px;
  transition: background 0.2s ease;
  flex-shrink: 0;
}

.custom-toggle.checked {
  background: var(--bs-primary);
}

.toggle-knob {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease;
}

.custom-toggle.checked .toggle-knob {
  transform: translateX(16px);
}
</style>